import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import Pusher from 'pusher-js';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../api'; // Import your API utility
import {
  companyChannel,
  opsChannel,
  threadChannel,
  userChannel,
} from '@dreamai/types';
import {
  createChannelAuthorizationHandler,
  logSubscriptionErrors,
  PUSHER_ENV,
} from '../lib/pusherClient';

const PusherContext = createContext();

/**
 * Hook to access Pusher functionality
 * @param {boolean} [full=false] - If true, returns the full context object with all properties
 * @returns {(Pusher.Channel|{pusherChannel: Pusher.Channel, pusherInstance: Pusher, token: string, subscribeToThreadChannel: Function, unsubscribeFromThreadChannel: Function})} - Either just the Pusher channel (default) or the full context object with streaming functionality
 */
export const usePusher = (full = false) => {
  const context = useContext(PusherContext);

  if (!context) return null;

  // If full is true, return the entire context object
  if (full) {
    return context;
  }

  // Otherwise, return just the channel for backward compatibility
  return context.pusherChannel;
};

export const PusherProvider = ({ children }) => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const [pusherChannel, setPusherChannel] = useState(null);
  const [userPusherChannel, setUserPusherChannel] = useState(null);
  const [opsPusherChannel, setOpsPusherChannel] = useState(null);
  const [pusherInstance, setPusherInstance] = useState(null);
  const [token, setToken] = useState(null);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [companyId, setCompanyId] = useState(null);
  const [isOpsInternalUser, setIsOpsInternalUser] = useState(false);
  const heartbeatIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isConnectingRef = useRef(false);

  // Function to establish Pusher connection
  const connectPusher = useCallback(async () => {
    if (!isAuthenticated) return null;

    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current) {
      console.log('Already attempting to connect, skipping duplicate request');
      return pusherInstance;
    }

    // If we already have a connected instance, just return it
    if (pusherInstance && pusherInstance.connection.state === 'connected') {
      console.log('Reusing existing connected Pusher instance');
      return pusherInstance;
    }

    // Set connecting flag
    isConnectingRef.current = true;

    try {
      const accessToken = await getAccessTokenSilently();
      setToken(accessToken);

      let company_id = companyId;
      let isOpsUser = isOpsInternalUser;
      if (!company_id) {
        const response = await api.get('/confirm-user-company', {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        company_id = response.data.company_id;
        isOpsUser = response.data.isOpsInternalUser || false;
        setCompanyId(company_id);
        setIsOpsInternalUser(isOpsUser);
      }

      // Clean up any existing connection that's not in a good state
      if (pusherInstance && pusherInstance.connection.state !== 'connected') {
        console.log('Cleaning up existing problematic Pusher connection');
        try {
          pusherInstance.disconnect();
        } catch (err) {
          console.warn('Error while disconnecting old instance:', err);
        }
      }

      // Initialize Pusher with proper authentication
      const instance = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER,
        channelAuthorization: {
          customHandler: createChannelAuthorizationHandler(
            getAccessTokenSilently,
            process.env.REACT_APP_API_URL,
          ),
        },
        enabledTransports: ['ws', 'wss'],
        forceTLS: true,
        // Add activity timeout to detect disconnections faster
        activityTimeout: 30000, // 30 seconds
        pongTimeout: 10000, // How long to wait for a pong response (default 30s)
      });
      logSubscriptionErrors(instance);

      // Add connection event handlers for monitoring connection state
      instance.connection.bind('connected', () => {
        console.log('Pusher connected successfully');
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
        monitorConnectionState(instance);
        isConnectingRef.current = false;
      });

      instance.connection.bind('connecting', () => {
        console.log('Pusher connecting...');
        setConnectionState('connecting');
      });

      instance.connection.bind('disconnected', () => {
        console.log('Pusher disconnected');
        setConnectionState('disconnected');
        isConnectingRef.current = false;
      });

      instance.connection.bind('error', (err) => {
        console.error('Pusher connection error:', err);
        setConnectionState('error');
        isConnectingRef.current = false;
      });

      setPusherInstance(instance);

      const channel = instance.subscribe(
        companyChannel(PUSHER_ENV, company_id),
      );
      setPusherChannel(channel);

      // Subscribe to user-specific channel for Chrome notifications and user-specific events
      const userChan = instance.subscribe(userChannel(PUSHER_ENV, user.sub));
      setUserPusherChannel(userChan);

      // Subscribe to ops channel if user is an internal ops user
      if (isOpsUser) {
        const opsChan = instance.subscribe(opsChannel(PUSHER_ENV));
        setOpsPusherChannel(opsChan);
        console.log('Subscribed to ops channel');
      }

      return instance;
    } catch (error) {
      console.error('Error setting up Pusher:', error);
      setConnectionState('error');
      isConnectingRef.current = false;
      return null;
    }
  }, [isAuthenticated, getAccessTokenSilently, pusherInstance, companyId]);

  // Monitor connection state changes and rely on Pusher's built-in connection management
  const monitorConnectionState = useCallback((instance) => {
    // Clear any existing connection state checks
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Log connection state changes and handle reconnection
    instance.connection.bind('state_change', (states) => {
      const { previous, current } = states;
      console.log(
        `Pusher connection state changed from ${previous} to ${current}`,
      );
      setConnectionState(current);

      // If disconnected, attempt to reconnect
      if (current === 'disconnected' || current === 'failed') {
        reconnect();
      }

      // If connected, reset reconnect attempts
      if (current === 'connected') {
        reconnectAttemptsRef.current = 0;
      }
    });
  }, []);

  // Function to reconnect Pusher with exponential backoff
  const reconnect = useCallback(() => {
    // If we're already connecting, don't try again
    if (isConnectingRef.current) {
      console.log('Already attempting to connect, skipping reconnect');
      return;
    }

    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Implement exponential backoff
    const attempts = reconnectAttemptsRef.current;
    reconnectAttemptsRef.current++;

    // Calculate delay: 1s, 2s, 4s, 8s, etc. up to 30s max
    const delay = Math.min(Math.pow(2, attempts) * 1000, 30000);

    console.log(
      `Attempting to reconnect to Pusher in ${delay / 1000}s (attempt ${attempts + 1})...`,
    );

    // Schedule reconnection attempt
    reconnectTimeoutRef.current = setTimeout(() => {
      connectPusher();
    }, delay);
  }, [connectPusher]);

  // Check connection before any message send
  const ensureConnection = useCallback(async () => {
    // If we're already connected, return immediately
    if (pusherInstance && pusherInstance.connection.state === 'connected') {
      console.log('Pusher already connected, proceeding with message');
      return true;
    }

    // If we're connecting, wait a bit to see if it completes
    if (isConnectingRef.current) {
      console.log('Connection attempt in progress, waiting...');
      // Wait for existing connection attempt to complete
      return new Promise((resolve) => {
        let attempts = 0;
        const checkExistingConnection = () => {
          attempts++;
          if (
            pusherInstance &&
            pusherInstance.connection.state === 'connected'
          ) {
            console.log('Existing connection attempt succeeded');
            resolve(true);
          } else if (attempts > 50) {
            // 5 seconds max wait
            console.log('Timed out waiting for existing connection');
            resolve(false);
          } else {
            setTimeout(checkExistingConnection, 100);
          }
        };
        checkExistingConnection();
      });
    }

    console.log('Connection needs to be established before sending message');
    // If we're not connected, reconnect and wait for connection
    const instance = await connectPusher();

    // Wait for connection to establish (with timeout)
    if (instance) {
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds total

        const checkConnection = () => {
          attempts++;
          if (instance.connection.state === 'connected') {
            resolve(true);
          } else if (attempts >= maxAttempts) {
            console.log('Connection timed out after 5 seconds');
            resolve(false);
          } else {
            // Check again in 100ms
            setTimeout(checkConnection, 100);
          }
        };

        // Start checking
        checkConnection();
      });
    }

    return false;
  }, [connectPusher, pusherInstance]);

  // Set up initial connection and handle visibility changes
  useEffect(() => {
    // Initial connection
    connectPusher();

    // Handle visibility changes (user returns to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible, checking connection...');
        if (
          !pusherInstance ||
          pusherInstance.connection.state !== 'connected'
        ) {
          reconnect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (pusherInstance) {
        pusherInstance.disconnect();
      }
    };
  }, [connectPusher, reconnect, pusherInstance]);

  // Cache for thread channels to ensure components use the same channel instance
  const threadChannelCache = useRef(new Map());

  /**
   * Subscribe to a thread channel for agent streaming
   * @param {string} threadId - The thread ID to subscribe to
   * @returns {Object} The Pusher channel for the thread
   */
  const subscribeToThreadChannel = (threadId) => {
    if (!pusherInstance || !threadId) return null;

    try {
      // Check if we already have a channel for this thread in the cache
      if (threadChannelCache.current.has(threadId)) {
        console.log('[Workbox]: Using cached channel for thread:', threadId);
        return threadChannelCache.current.get(threadId);
      }

      // Make sure we're authenticated before subscribing to private channels
      const channelName = threadChannel(PUSHER_ENV, threadId);
      console.log('[Workbox]: Subscribing to new channel:', channelName);
      const channel = pusherInstance.subscribe(channelName);

      // Store the channel in the cache
      threadChannelCache.current.set(threadId, channel);

      // Debug to verify the channel is properly created
      console.log('[Workbox]: Channel object:', channel);
      return channel;
    } catch (error) {
      console.error('[Workbox]: Error subscribing to thread channel:', error);
      return null;
    }
  };

  /**
   * Unsubscribe from a thread channel
   * @param {string} threadId - The thread ID to unsubscribe from
   * @param {boolean} removeFromCache - Whether to remove the channel from the cache
   */
  const unsubscribeFromThreadChannel = (threadId, removeFromCache = false) => {
    if (!pusherInstance || !threadId) return;

    const channelName = threadChannel(PUSHER_ENV, threadId);
    console.log(
      `Unsubscribing from channel: ${channelName}, removeFromCache: ${removeFromCache}`,
    );

    // Unsubscribe from the channel
    pusherInstance.unsubscribe(channelName);

    // Remove from cache if requested
    if (removeFromCache && threadChannelCache.current.has(threadId)) {
      console.log(`Removing channel from cache: ${threadId}`);
      threadChannelCache.current.delete(threadId);
    }
  };

  // Create a context value object with all the properties
  const contextValue = {
    pusherChannel,
    userChannel: userPusherChannel,
    opsChannel: opsPusherChannel,
    pusherInstance,
    token,
    connectionState,
    subscribeToThreadChannel,
    unsubscribeFromThreadChannel,
    ensureConnection,
    reconnect,
  };

  return (
    <PusherContext.Provider value={contextValue}>
      {children}
    </PusherContext.Provider>
  );
};
